"use client"

import * as React from "react"
import type { FinanceHomeWeek } from "@ibada/shared-types/overview"

import { calendarDayToDate, chartAmountLabel, mirroredBars } from "@/features/dashboard/home-d4/lib/home-format"
import { useLocale, useTranslations } from "@/i18n"
import { formatCurrency } from "@/shared/lib/formatting/currency"

const WIDTH = 520
const HEIGHT = 156
const MID = (HEIGHT - 16) / 2
/** Room above and below the bars for the direct labels on the latest week. */
const LABEL_ROOM = 16
const MINUS = "−"

/**
 * Income above the baseline, expenses below it, one shared scale, so the two series never need a second axis
 * and stay distinguishable without colour (position carries the identity). The latest week is drawn solid with
 * direct labels; earlier weeks recede. Each week has a hover target larger than its bars with the exact amounts.
 */
export function MirroredWeeklyChart({ weeks, currency }: { weeks: FinanceHomeWeek[]; currency: string }) {
  const t = useTranslations("dashboard.home.treasury")
  const locale = useLocale()
  const [hovered, setHovered] = React.useState<number | null>(null)
  const bars = mirroredBars(weeks)
  const count = bars.length
  if (count === 0) return null

  const slot = (WIDTH - 40) / count
  const barWidth = Math.min(28, slot * 0.42)
  const reach = MID - LABEL_ROOM
  const dayFormat = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" })
  const latest = bars[count - 1]!
  const latestX = 20 + (count - 1) * slot + slot / 2

  return (
    <div className='relative min-h-0 flex-1'>
      <svg
        role='img'
        aria-label={t("chartLabel")}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className='block h-full max-h-56 w-full'
        preserveAspectRatio='xMidYMid meet'
        data-testid='mirrored-weekly-chart'
        onMouseLeave={() => setHovered(null)}
      >
        {bars.map((bar, index) => {
          const x = 20 + index * slot + slot / 2 - barWidth / 2
          const isLatest = index === count - 1
          const dim = hovered !== null ? hovered !== index : !isLatest
          const incomeHeight = bar.incomeRatio * reach
          const expenseHeight = bar.expenseRatio * reach
          return (
            <g key={bar.weekStart}>
              {incomeHeight > 0 ? (
                <path
                  d={roundedTop(x, MID - 1 - incomeHeight, barWidth, incomeHeight)}
                  className='fill-teal-600 dark:fill-teal-400'
                  opacity={dim ? 0.5 : 1}
                />
              ) : null}
              {expenseHeight > 0 ? (
                <path
                  d={roundedBottom(x, MID + 1, barWidth, expenseHeight)}
                  className='fill-rose-600 dark:fill-rose-400'
                  opacity={dim ? 0.45 : 1}
                />
              ) : null}
              <text
                x={x + barWidth / 2}
                y={HEIGHT - 2}
                textAnchor='middle'
                className='fill-muted-foreground'
                fontSize={10}
              >
                {dayFormat.format(calendarDayToDate(bar.weekStart))}
              </text>
              {/* The hover target spans the whole week column, not just the bars. */}
              <rect
                x={20 + index * slot}
                y={0}
                width={slot}
                height={HEIGHT - 14}
                fill='transparent'
                onMouseEnter={() => setHovered(index)}
                onFocus={() => setHovered(index)}
                tabIndex={0}
                aria-label={t("weekTooltip", {
                  week: dayFormat.format(calendarDayToDate(bar.weekStart)),
                  income: formatCurrency(bar.income, currency),
                  expense: formatCurrency(bar.expense, currency),
                })}
              />
            </g>
          )
        })}
        <line x1={14} x2={WIDTH - 6} y1={MID} y2={MID} className='stroke-border' strokeWidth={1} />
        {latest.incomeRatio > 0 ? (
          <text
            data-testid='chart-label-income'
            x={latestX}
            y={MID - 6 - latest.incomeRatio * reach}
            textAnchor='middle'
            fontSize={11}
            fontWeight={700}
            className='pointer-events-none fill-foreground'
          >
            {`+${chartAmountLabel(latest.income, locale)}`}
          </text>
        ) : null}
        {latest.expenseRatio > 0 ? (
          <text
            data-testid='chart-label-expense'
            x={latestX}
            y={MID + 13 + latest.expenseRatio * reach}
            textAnchor='middle'
            fontSize={11}
            fontWeight={700}
            className='pointer-events-none fill-foreground'
          >
            {`${MINUS}${chartAmountLabel(latest.expense, locale)}`}
          </text>
        ) : null}
      </svg>
      {hovered !== null && bars[hovered] ? (
        <div
          role='tooltip'
          className='pointer-events-none absolute top-0 z-10 rounded-2xl whitespace-nowrap border border-border bg-popover px-2.5 py-1.5 text-xs shadow-md'
          style={{ left: `${((20 + hovered * slot + slot / 2) / WIDTH) * 100}%`, transform: "translateX(-50%)" }}
        >
          <p className='font-bold text-foreground'>
            {t("weekOf", { week: dayFormat.format(calendarDayToDate(bars[hovered]!.weekStart)) })}
          </p>
          <p className='text-muted-foreground tabular-nums'>
            {t("legendIncome")} <span className='font-semibold text-foreground'>{formatCurrency(bars[hovered]!.income, currency)}</span>
          </p>
          <p className='text-muted-foreground tabular-nums'>
            {t("legendExpense")} <span className='font-semibold text-foreground'>{formatCurrency(bars[hovered]!.expense, currency)}</span>
          </p>
        </div>
      ) : null}
    </div>
  )
}

/** A bar whose data end (the top) is rounded and whose baseline end is square. */
function roundedTop(x: number, y: number, width: number, height: number): string {
  const r = Math.min(4, width / 2, height)
  return `M${x} ${y + height} V${y + r} Q${x} ${y} ${x + r} ${y} H${x + width - r} Q${x + width} ${y} ${x + width} ${y + r} V${y + height} Z`
}

/** Mirror of {@link roundedTop}: rounded at the bottom, square at the baseline. */
function roundedBottom(x: number, y: number, width: number, height: number): string {
  const r = Math.min(4, width / 2, height)
  return `M${x} ${y} H${x + width} V${y + height - r} Q${x + width} ${y + height} ${x + width - r} ${y + height} H${x + r} Q${x} ${y + height} ${x} ${y + height - r} Z`
}
